import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CompatibilityWarnings = ({ warnings, onResolve, onIgnore }) => {
  if (!warnings || warnings?.length === 0) return null;

  const getWarningIcon = (severity) => {
    switch (severity) {
      case 'error':
        return { name: 'XCircle', color: 'text-error' };
      case 'warning':
        return { name: 'AlertTriangle', color: 'text-warning' };
      case 'info':
        return { name: 'Info', color: 'text-primary' };
      default:
        return { name: 'AlertCircle', color: 'text-muted-foreground' };
    }
  };

  const getSeverityBg = (severity) => {
    switch (severity) {
      case 'error':
        return 'bg-error/10 border-error/20';
      case 'warning':
        return 'bg-warning/10 border-warning/20';
      case 'info':
        return 'bg-primary/10 border-primary/20';
      default:
        return 'bg-muted border-border';
    }
  };

  return (
    <div className="space-y-3">
      {warnings?.map((warning) => {
        const icon = getWarningIcon(warning?.severity);
        const bgClass = getSeverityBg(warning?.severity);

        return (
          <div
            key={warning?.id}
            className={`border rounded-lg p-4 ${bgClass}`}
          >
            <div className="flex items-start space-x-3">
              <Icon
                name={icon?.name}
                size={20}
                className={`${icon?.color} flex-shrink-0 mt-0.5`}
              />
              
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h4 className="text-sm font-semibold text-foreground">
                      {warning?.title}
                    </h4>
                    <p className="text-sm text-muted-foreground mt-1">
                      {warning?.description}
                    </p>

                    {/* Affected Items */}
                    {warning?.affectedItems && warning?.affectedItems?.length > 0 && (
                      <div className="mt-3">
                        <span className="text-xs font-medium text-foreground">
                          Affected items:
                        </span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {warning?.affectedItems?.map((item, index) => (
                            <span
                              key={index}
                              className="inline-flex items-center px-2 py-1 bg-background rounded text-xs text-muted-foreground"
                            >
                              {item}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Suggested Solutions */}
                    {warning?.suggestions && warning?.suggestions?.length > 0 && (
                      <div className="mt-3">
                        <span className="text-xs font-medium text-foreground">
                          Suggested solutions:
                        </span>
                        <ul className="mt-1 space-y-1">
                          {warning?.suggestions?.map((suggestion, index) => (
                            <li key={index} className="text-xs text-muted-foreground flex items-start space-x-1">
                              <span className="text-primary">•</span>
                              <span>{suggestion}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  {/* Severity Badge */}
                  <div className="flex-shrink-0 ml-3">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      warning?.severity === 'error' ? 'bg-error text-error-foreground' :
                      warning?.severity === 'warning' ? 'bg-warning text-warning-foreground' :
                      warning?.severity === 'info' ? 'bg-primary text-primary-foreground' :
                      'bg-muted text-muted-foreground'
                    }`}>
                      {warning?.severity?.toUpperCase()}
                    </span>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center space-x-2 mt-4">
                  {warning?.canResolve && (
                    <Button
                      variant="outline"
                      size="xs"
                      onClick={() => onResolve(warning?.id)}
                    >
                      <Icon name="Wrench" size={14} />
                      <span className="ml-1">Auto-fix</span>
                    </Button>
                  )}
                  
                  {warning?.hasAlternatives && (
                    <Button
                      variant="outline"
                      size="xs"
                    >
                      <Icon name="RefreshCw" size={14} />
                      <span className="ml-1">Find Alternatives</span>
                    </Button>
                  )}
                  
                  <Button
                    variant="ghost"
                    size="xs"
                    onClick={() => onIgnore(warning?.id)}
                  >
                    <Icon name="X" size={14} />
                    <span className="ml-1">Ignore</span>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default CompatibilityWarnings;